/**
 * Central route access registry for sec_groups_apps enforcement.
 *
 * Primary source: routes.js (see utils/buildRouteAccessCatalog.js).
 * Scopes are hierarchical: granting scope "customers" applies to every matching route
 * under that scope, including direct deep links.
 *
 * Store scope values in sec_groups_apps.app_name (e.g. "customers", not "customers/list").
 */

import {
  buildRouteAccessCatalogFromRoutes,
  buildSecAccessScopesFromCatalog,
  routePathToSecAppName,
} from "utils/buildRouteAccessCatalog";

export {
  routePathToSecAppName,
  resolveScopeForRoute,
  shouldIncludeRouteInAccessCatalog,
  buildRouteAccessCatalogFromRoutes,
  buildSecAccessScopesFromCatalog,
  isAlwaysAccessiblePath,
  isAlwaysAccessibleRouteConfig,
} from "utils/buildRouteAccessCatalog";

/** @type {Array<{ section: string, label: string, route: string, appName: string, scope: string }>|null} */
let cachedRouteCatalog = null;
/** @type {Array<{ scope: string, section: string, label: string, description: string }>|null} */
let cachedAccessScopes = null;

function ensureRouteAccessCache() {
  if (cachedRouteCatalog && cachedAccessScopes) {
    return;
  }

  // Lazy require avoids circular imports: routes.js → layouts → GroupAccessContext → secAppAccess → here.
  // eslint-disable-next-line global-require
  const routesModule = require("routes");
  const routeTree = routesModule.routes || routesModule.default;
  cachedRouteCatalog = buildRouteAccessCatalogFromRoutes(routeTree);
  cachedAccessScopes = buildSecAccessScopesFromCatalog(cachedRouteCatalog);
}

/** Route catalog derived from routes.js. */
export function getRouteAccessCatalog() {
  ensureRouteAccessCache();
  return cachedRouteCatalog || [];
}

/** Assignable scopes for Users and Groups — derived from routes.js. */
export function getSecAccessScopes() {
  ensureRouteAccessCache();
  return cachedAccessScopes || [];
}

/** @deprecated Use getRouteAccessCatalog() */
export function getSecAppRouteCatalog() {
  return getRouteAccessCatalog();
}

/** Known AdminZone scope names (sec_groups_apps.app_name values). */
export function getSecAccessScopeNames() {
  return new Set(getSecAccessScopes().map(({ scope }) => scope));
}

/**
 * @param {string|null|undefined} appName
 * @returns {boolean}
 */
export function isCatalogScope(appName) {
  return getSecAccessScopeNames().has(routePathToSecAppName(appName));
}

/**
 * @param {string|null|undefined} secAppName
 * @returns {RegExp|null}
 */
export function secAppNameToPathRegex(secAppName) {
  const normalized = routePathToSecAppName(secAppName);
  if (!normalized) return null;
  const pattern = normalized.replace(/:[^/]+/g, "[^/]+").replace(/\*/g, ".*");
  return new RegExp(`^${pattern}$`);
}

/**
 * @param {string} pathname
 * @param {string} secAppName
 * @returns {boolean}
 */
export function pathnameMatchesSecAppName(pathname, secAppName) {
  const regex = secAppNameToPathRegex(secAppName);
  if (!regex) return false;
  return regex.test(routePathToSecAppName(pathname));
}

/**
 * Scope prefix match: scope "customers" applies to "customers/list" and "customers/view/1".
 * @param {string} scope
 * @param {string} pathname
 * @returns {boolean}
 */
export function scopeAppliesToPath(scope, pathname) {
  const scopeText = routePathToSecAppName(scope);
  const pathText = routePathToSecAppName(pathname);
  if (!scopeText || !pathText) return false;
  if (pathText === scopeText) return true;
  return pathText.startsWith(`${scopeText}/`);
}

/**
 * @param {string} pathname
 * @returns {object|null}
 */
export function matchCatalogEntryForPath(pathname) {
  const pathText = routePathToSecAppName(pathname);
  if (!pathText) return null;

  let best = null;
  let bestScore = -1;

  getRouteAccessCatalog().forEach((entry) => {
    const routePattern = routePathToSecAppName(entry.route);
    if (!routePattern) return;
    const regex = secAppNameToPathRegex(routePattern);
    if (!regex || !regex.test(pathText)) return;

    const score = routePattern.replace(/:[^/]+/g, "").length;
    if (score > bestScore) {
      bestScore = score;
      best = entry;
    }
  });

  return best;
}

/**
 * Access keys for a pathname, most specific first then scope ancestors.
 * @param {string} pathname
 * @returns {string[]}
 */
export function resolvePathAccessKeys(pathname) {
  const keys = [];
  const entry = matchCatalogEntryForPath(pathname);
  const pathText = routePathToSecAppName(pathname);

  if (entry?.appName) {
    keys.push(routePathToSecAppName(entry.appName));
  }
  if (entry?.scope) {
    keys.push(routePathToSecAppName(entry.scope));
  }
  if (pathText && !keys.includes(pathText)) {
    keys.push(pathText);
  }

  getSecAccessScopes().forEach(({ scope }) => {
    if (scopeAppliesToPath(scope, pathname) && !keys.includes(scope)) {
      keys.push(scope);
    }
  });

  return keys;
}

/**
 * Whether a sec_groups_apps.app_name rule applies to the current pathname.
 * @param {string} restrictedAppName
 * @param {string} pathname
 * @returns {boolean}
 */
export function restrictedRuleAppliesToPath(restrictedAppName, pathname) {
  const rule = routePathToSecAppName(restrictedAppName);
  if (!rule) return false;

  const pathKeys = resolvePathAccessKeys(pathname);
  if (pathKeys.includes(rule)) return true;
  if (pathnameMatchesSecAppName(pathname, rule)) return true;
  if (scopeAppliesToPath(rule, pathname)) return true;

  const entry = matchCatalogEntryForPath(pathname);
  if (entry?.scope && entry.scope === rule) return true;

  return false;
}

/**
 * Restricted rules from sec_groups_apps that apply to this pathname.
 * @param {string} pathname
 * @param {string[]} restrictedAppNames
 * @returns {string[]}
 */
export function findApplyingRestrictedRules(pathname, restrictedAppNames) {
  const restricted = Array.isArray(restrictedAppNames) ? restrictedAppNames : [];
  const applying = restricted.filter((rule) => restrictedRuleAppliesToPath(rule, pathname));
  return Array.from(new Set(applying.map((rule) => routePathToSecAppName(rule)).filter(Boolean)));
}

/**
 * Resolve access keys for a sidenav route config entry.
 * @param {{ route?: string, secAppName?: string, secScope?: string }|null|undefined} routeConfig
 * @returns {string[]}
 */
export function resolveRouteConfigAccessKeys(routeConfig) {
  if (!routeConfig) return [];
  const keys = [];
  if (routeConfig.secScope) {
    keys.push(routePathToSecAppName(routeConfig.secScope));
  }
  if (routeConfig.secAppName) {
    keys.push(routePathToSecAppName(routeConfig.secAppName));
  }
  if (routeConfig.route) {
    keys.push(...resolvePathAccessKeys(routeConfig.route));
  }
  return Array.from(new Set(keys.filter(Boolean)));
}

/**
 * @param {string} scope
 * @returns {{ scope: string, section: string, label: string, description?: string }|null}
 */
export function getScopeDefinition(scope) {
  const normalized = routePathToSecAppName(scope);
  return getSecAccessScopes().find((entry) => entry.scope === normalized) || null;
}

/**
 * Resolve the catalog scope that governs a pathname (default-deny enforcement target).
 * @param {string} pathname
 * @returns {string|null}
 */
export function resolveCatalogScopeForPath(pathname) {
  const entry = matchCatalogEntryForPath(pathname);
  if (entry?.scope) {
    return routePathToSecAppName(entry.scope);
  }

  const pathText = routePathToSecAppName(pathname);
  if (!pathText) return null;

  let bestScope = null;
  let bestLength = -1;

  getSecAccessScopes().forEach(({ scope }) => {
    if (!scopeAppliesToPath(scope, pathname)) return;
    if (scope.length > bestLength) {
      bestLength = scope.length;
      bestScope = scope;
    }
  });

  return bestScope;
}

/**
 * @param {{ route?: string, secAppName?: string, secScope?: string }|null|undefined} routeConfig
 * @returns {string|null}
 */
export function resolveCatalogScopeForRouteConfig(routeConfig) {
  if (!routeConfig) return null;
  if (routeConfig.secScope) {
    const scope = routePathToSecAppName(routeConfig.secScope);
    return isCatalogScope(scope) ? scope : null;
  }
  if (routeConfig.route) {
    return resolveCatalogScopeForPath(routeConfig.route);
  }
  return null;
}

/** Resets cached route catalog (tests). */
export function resetRouteAccessCache() {
  cachedRouteCatalog = null;
  cachedAccessScopes = null;
}
